import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { INTEGRATION_PLATFORMS } from '../../api/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { readBillingSettings, savePriceList, saveSupplierProfile } from '../../api/admin/endpoints';
import type { PriceList, SupplierProfile } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import {
  PRICE_METRICS,
  priceListDraftFrom,
  priceListInputFrom,
  supplierDraftFrom,
  supplierInputFrom,
  type PriceListDraft,
  type SupplierDraft,
} from '../../components/billingSettingsDraft';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { useT } from '../../i18n/LanguageContext';
import type { MessageKey } from '../../i18n/sk';

const METRIC_LABELS: Record<(typeof PRICE_METRICS)[number], MessageKey> = {
  companies: 'licence.metric.companies',
  subUsers: 'licence.metric.subUsers',
  products: 'licence.metric.products',
  totalStock: 'licence.metric.totalStock',
};

type TextKey = Exclude<keyof SupplierDraft, 'vatPayer'>;

/** The supplier every document prints and the price list every licence is sold at (R0006 D-01, D-02). */
export const SettingsPage = () => {
  const t = useT();
  const query = useQuery({ queryKey: ['billing-settings'], queryFn: readBillingSettings });
  const [supplier, setSupplier] = useState<SupplierDraft | null>(null);
  const [prices, setPrices] = useState<PriceListDraft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<'supplier' | 'prices' | null>(null);

  useEffect(() => {
    if (query.data) {
      setSupplier(supplierDraftFrom(query.data.supplier));
      setPrices(priceListDraftFrom(query.data.priceList));
    }
  }, [query.data]);

  const saveSupplier = useAppMutation(
    (profile: SupplierProfile) => saveSupplierProfile(profile),
    ['billing-settings', 'audit'],
    () => setSaved('supplier'),
  );
  const savePrices = useAppMutation(
    (priceList: PriceList) => savePriceList(priceList),
    ['billing-settings', 'audit'],
    () => setSaved('prices'),
  );

  if (query.isLoading || !supplier || !prices) {
    return query.error ? (
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
    ) : (
      <Skeleton variant="rounded" height={420} />
    );
  }

  const field = (
    key: TextKey,
    label: MessageKey,
    extra: { required?: boolean; helper?: MessageKey } = {},
  ) => (
    <TextField
      fullWidth
      required={extra.required ?? false}
      label={t(label)}
      helperText={extra.helper ? t(extra.helper) : undefined}
      value={supplier[key]}
      onChange={(event) => {
        setSaved(null);
        setSupplier({ ...supplier, [key]: event.target.value });
      }}
    />
  );

  const submitSupplier = (): void => {
    const input = supplierInputFrom(supplier);

    setProblem(input === null ? t('settings.invalid') : null);

    if (input !== null) {
      saveSupplier.mutate(input);
    }
  };

  const submitPrices = (): void => {
    const input = priceListInputFrom(prices);

    setProblem(input === null ? t('settings.invalid') : null);

    if (input !== null) {
      savePrices.mutate(input);
    }
  };

  const missing = query.data?.supplier === null || query.data?.priceList === null;

  return (
    <>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <Stack spacing={3}>
        {missing ? (
          <Alert severity="warning" variant="outlined">
            {t('settings.missing')}
          </Alert>
        ) : null}
        {problem ? (
          <Alert severity="warning" variant="outlined">
            {problem}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6">{t('settings.supplier')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.supplierHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>{field('name', 'field.legalName', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 6 }}>{field('street', 'field.street', { required: true })}</Grid>
            <Grid size={{ xs: 6, md: 3 }}>{field('postalCode', 'field.postalCode', { required: true })}</Grid>
            <Grid size={{ xs: 6, md: 6 }}>{field('city', 'field.city', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('country', 'field.country', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              {field('registrationNumber', 'field.registrationNumber', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>{field('taxNumber', 'field.taxNumber', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              {field('vatNumber', 'field.vatNumber', { required: supplier.vatPayer })}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={supplier.vatPayer}
                    onChange={(event) => {
                      setSaved(null);
                      setSupplier({ ...supplier, vatPayer: event.target.checked });
                    }}
                  />
                }
                label={t('field.vatPayer')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {supplier.vatPayer ? field('vatRate', 'field.vatRate', { required: true }) : null}
            </Grid>
            <Grid size={12}>
              {field('registerEntry', 'field.registerEntry', {
                required: true,
                helper: 'field.registerEntryHint',
              })}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>{field('email', 'field.billingEmail')}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>{field('phone', 'field.phone')}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>{field('web', 'field.web')}</Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('settings.bank')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>{field('iban', 'field.iban', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('swift', 'field.swift')}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('bankName', 'field.bankName')}</Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('settings.numbering')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>{field('proformaPrefix', 'field.proformaPrefix')}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('invoicePrefix', 'field.invoicePrefix')}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('dueDays', 'field.dueDays', { required: true })}</Grid>
            <Grid size={{ xs: 12, md: 3 }}>{field('issuedBy', 'field.issuedBy')}</Grid>
          </Grid>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <Button variant="contained" disabled={saveSupplier.isPending} onClick={submitSupplier}>
              {t('common.save')}
            </Button>
            {saved === 'supplier' ? (
              <Typography variant="body2" color="success.main">
                {t('settings.saved')}
              </Typography>
            ) : null}
          </Stack>
          <Box sx={{ mt: 2 }}>
            <ErrorNotice error={saveSupplier.error} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6">{t('settings.priceList')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.priceListHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('price.base')}
                value={prices.base}
                inputProps={{ inputMode: 'decimal' }}
                onChange={(event) => {
                  setSaved(null);
                  setPrices({ ...prices, base: event.target.value });
                }}
              />
            </Grid>
            {INTEGRATION_PLATFORMS.map((platform) => (
              <Grid key={platform} size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('price.integrationLink', { platform: t(`platform.${platform}`) })}
                  helperText={t('price.integrationLinkHint')}
                  value={prices.linkPrices[platform]}
                  inputProps={{ inputMode: 'decimal' }}
                  onChange={(event) => {
                    setSaved(null);
                    setPrices({
                      ...prices,
                      linkPrices: { ...prices.linkPrices, [platform]: event.target.value },
                    });
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {PRICE_METRICS.map((metric) => (
            <Box key={metric} sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(METRIC_LABELS[metric])}
              </Typography>
              <Grid container spacing={2}>
                {(
                  [
                    ['unitPrice', 'price.unit', 'decimal'],
                    ['unitSize', 'price.unitSize', 'numeric'],
                    ['unlimitedPrice', 'price.unlimited', 'decimal'],
                  ] as const
                ).map(([key, label, mode]) => (
                  <Grid key={key} size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={t(label)}
                      value={prices[metric][key]}
                      inputProps={{ inputMode: mode }}
                      onChange={(event) => {
                        setSaved(null);
                        setPrices({ ...prices, [metric]: { ...prices[metric], [key]: event.target.value } });
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <Button variant="contained" disabled={savePrices.isPending} onClick={submitPrices}>
              {t('common.save')}
            </Button>
            {saved === 'prices' ? (
              <Typography variant="body2" color="success.main">
                {t('settings.saved')}
              </Typography>
            ) : null}
          </Stack>
          <Box sx={{ mt: 2 }}>
            <ErrorNotice error={savePrices.error} />
          </Box>
        </Paper>
      </Stack>
    </>
  );
};
